// the Booking deals board "+ New offer" quick-create. Creates an in-band OPPORTUNITY
// The opportunity endpoint itself stays MONEY-FREE (its DTO carries no money field; the CD "offer"
// guarantee/probability are not served). The OPTIONAL money figure is a separate money-gated sub-resource
// chained after the save (`useSetOpportunityMoney`) and shown only to a money-team seat — the A3 rule: money
// is separate endpoints/DTOs, never a conditional field on the shared shape. The
// backend always seeds status='open', so a new deal lands in the board's first column. Server-confirm (NOT
// optimistic — a create mints a server id); the hook surfaces any error verbatim as a toast. The 's Google
// address autofill + the invite-contacts picker are recorded follow-ups (this is the required-fields quick form).
import { useId, useState, type FormEvent } from "react";
import { Button, Field, FormDialog, Picker, Textarea, DialogCancel } from "@cntrl/ui";
import {
  useCreateOpportunity,
  useUpdateOpportunityStatus,
  useSetOpportunityInvitees,
  useDeleteOpportunity,
  useReinviteOpportunity,
  type CreateOpportunityRequest,
  type UpdateOpportunityRequest,
} from "../../app/hooks/useOpportunities.js";
import {
  useOpportunityMoneyFor,
  useSetOpportunityMoney,
  canSeeOppMoney,
  centsToInput,
  inputToCents,
} from "../../app/hooks/useOpportunityMoney.js";
import { useBandContext } from "../../app/bandContext.js";
import { ConfirmDialog } from "../shared/ConfirmDialog.js";
import type { components } from "@cntrl/api-client";
import { CrewInviteePicker } from "../dashboard/CrewInviteePicker.js";
import { PR_SUBTYPE_LABELS, PR_FORMAT_LABELS } from "../dashboard/oppDisplay.js";
import styles from "./NewDealModal.module.scss";

// Rehearsal leads and is the default (the legacy modal's default too); Other is the catch-all, so it sits
// LAST rather than first — a picker that opens on "Other" invites everything to be logged as nothing.
const TYPES: { value: string; label: string }[] = [
  { value: "rehearsal", label: "Rehearsal" },
  { value: "content", label: "Content" },
  { value: "pr", label: "PR" },
  { value: "other", label: "Other" },
];

// Built from the SHARED label maps (oppDisplay) so the picker and the card meta can never disagree — the
// insertion order of the map IS the option order.
const PR_SUBTYPES: { value: string; label: string }[] =
  Object.entries(PR_SUBTYPE_LABELS).map(([value, label]) => ({ value, label }));

const PR_FORMATS: { value: string; label: string }[] =
  Object.entries(PR_FORMAT_LABELS).map(([value, label]) => ({ value, label }));

type OpportunityDto = components["schemas"]["OpportunityDto"];

export interface NewDealModalProps {
  onClose: () => void;
  /** Pass an existing opportunity to EDIT it (a full-replace PUT); omit to create. */
  opp?: OpportunityDto;
  /** Modal chrome — the Booking board says "New deal"; the dashboard's + says "New opportunity". */
  title?: string;
  submitLabel?: string;
  /** Prefill the date on a CREATE (e.g. the calendar "+" seeds the tapped day). Ignored when editing —
   *  `opp?.date` wins there. */
  seedDate?: string;
}

export function NewDealModal({
  onClose,
  opp,
  title = "New deal",
  submitLabel = "Create",
  seedDate,
}: NewDealModalProps) {
  const create = useCreateOpportunity();
  const update = useUpdateOpportunityStatus();
  const setInviteeSet = useSetOpportunityInvitees();
  const reAsk = useReinviteOpportunity();
  const setMoney = useSetOpportunityMoney();
  const destroy = useDeleteOpportunity();
  const editing = !!opp;
  // The money gate. A money-less seat never renders the field and never calls the endpoint — the figure
  // simply isn't part of their form, and an edit they save leaves whatever the money team set untouched
  // (the money-free PUT does not carry money_cents, so there is nothing for them to clobber).
  const { moneyScope } = useBandContext();
  const canSeeMoney = canSeeOppMoney(moneyScope);
  // Read the CURRENT figure for this one opportunity (not off the paginated list — see the hook).
  const moneyQ = useOpportunityMoneyFor(
    opp?.opportunityId ?? undefined,
    canSeeMoney,
  );
  const originalCents = moneyQ.data?.moneyCents ?? null;
  const originalNotes = moneyQ.data?.moneyNotes ?? "";
  // Times arrive as "HH:MM:SS"; the <input type="time"> wants "HH:MM".
  const hhmm = (v: string | null | undefined) => (v ?? "").slice(0, 5);
  // Editing picks up the picker right where the create left off: the current in-band invitees, prefilled.
  // (A cross-band invite would carry inviteeBandId; those no longer exist, but filter defensively.)
  const originalInvitees = (opp?.invites ?? [])
    .filter((i) => i.inviteePersonId && !i.inviteeBandId)
    .map((i) => i.inviteePersonId!);
  const [invitees, setInvitees] = useState<string[]>(originalInvitees);
  /**
   * Who was X'd out of the list at some point in this session. Asking somebody again is remove-then-re-add
   * (PO, 2026-08-29), and doing both before pressing Save sends an invitee list IDENTICAL to the one the
   * modal opened with — so the two cancel out and the SET is not even called. This remembers the gesture
   * the payload cannot express; anyone still on the list at save time who appears here is genuinely being
   * asked again, and rides `POST /reinvite` (a real replacement: fresh invite row, fresh notification, an
   * accepted invitee's calendar entry taken back through the uninvite gate).
   */
  const [pulled, setPulled] = useState<string[]>([]);
  const changeInvitees = (next: string[]) => {
    const dropped = invitees.filter(
      (id) => !next.includes(id) && originalInvitees.includes(id),
    );
    if (dropped.length > 0)
      setPulled((prev) => [...new Set([...prev, ...dropped])]);
    setInvitees(next);
  };
  // Removed AND back on the list — the churn. Someone left off at save time is simply uninvited by the SET.
  const reAsked = invitees.filter((id) => pulled.includes(id));
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  // A create that LANDED but whose follow-ups did NOT. The opportunity exists from that moment on, so a
  // retry has to edit THAT one — a second `create` would mint a duplicate. This is not hypothetical: any
  // failing follow-up (a 404 off a stale API host, a dropped connection) leaves the form open on exactly
  // this state, and the obvious next click is Submit again.
  const [createdOppId, setCreatedOppId] = useState<string | null>(null);
  const [type, setType] = useState(opp?.type ?? "rehearsal");
  // `oppTitle`, not `title` — the component's own `title` prop is the MODAL HEADING.
  const [oppTitle, setOppTitle] = useState(opp?.title ?? "");
  const [date, setDate] = useState(opp?.date ?? seedDate ?? "");
  const [startTime, setStartTime] = useState(hhmm(opp?.startTime));
  const [endTime, setEndTime] = useState(hhmm(opp?.endTime));
  const [locationName, setLocationName] = useState(opp?.locationName ?? "");
  const [notes, setNotes] = useState(opp?.notes ?? "");
  const [prSubtype, setPrSubtype] = useState(opp?.prSubtype ?? "podcast");
  const [prFormat, setPrFormat] = useState(opp?.prFormat ?? "online");
  const [meetingLink, setMeetingLink] = useState(opp?.meetingLink ?? "");
  // `null` = untouched, so the field shows the fetched figure the moment it lands without an effect racing
  // the user's typing; once they type, their value wins for the rest of the modal's life.
  const [moneyEdit, setMoneyEdit] = useState<string | null>(null);
  const [moneyNotesEdit, setMoneyNotesEdit] = useState<string | null>(null);
  const money = moneyEdit ?? centsToInput(originalCents);
  const moneyNotes = moneyNotesEdit ?? originalNotes;

  const isPr = type === "pr";
  const isOnline = isPr && prFormat === "online";
  const setChanged =
    editing &&
    (invitees.length !== originalInvitees.length ||
      invitees.some((id) => !originalInvitees.includes(id)));
  // Emptying the set is not a save, it is a cancel: the backend requires ≥1 invitee on the set endpoint
  // (an empty set would orphan the opportunity to its creator) — the Cancel Opportunity button is that verb.
  const emptiedSet =
    setChanged && invitees.length === 0 && originalInvitees.length > 0;
  const moneyCents = canSeeMoney ? inputToCents(money) : null;
  const moneyNotesValue =
    canSeeMoney && moneyNotes.trim() !== "" ? moneyNotes.trim() : null;
  // The who-sees-this reassurance earns its space only once there IS money to see. A blank field (or an
  // explicit 0 — "this one pays nothing") has no audience question to answer, so the line stays out of the
  // form until the amount makes it relevant.
  const showMoneyHint = moneyCents != null && moneyCents > 0;
  // The hint is a sibling of the two fields rather than one field's own `hint`, so it can span the whole
  // row (see .moneyHint). That costs Field's automatic aria wiring, so it is wired by hand — the id is
  // passed back to the MONEY input, which is the control the line is about.
  const moneyHintId = useId();
  // Only WRITE money when it actually moved: an unchanged edit must not spend a money mutation (and an
  // audit row) on a no-op, and a create with blank fields must not stamp an explicit null. EITHER half
  // moving sends the pair — the PUT is the full money state, so a partial send would clear the other half.
  const moneyChanged =
    canSeeMoney &&
    (moneyCents !== (editing ? originalCents : null) ||
      moneyNotesValue !== (editing ? originalNotes.trim() || null : null));
  const busy =
    create.isPending ||
    update.isPending ||
    setInviteeSet.isPending ||
    setMoney.isPending ||
    reAsk.isPending;
  // Neither the date nor the start time is required. An opportunity with NO DATE is a real record — "they
  // want us on the podcast, date TBD" — and the reference's own specimen carries a `PR / No date` row; a
  // date-only opportunity (no clock yet) is likewise real, and the feed's expiry rule has always had a
  // branch for it. What an opportunity DOES need is something that names it, which is the legacy modal's
  // rule ("Add a title or an address") mapped onto our fields.
  // The reference's save rule — "title OR address" — mapped onto our pair: the opportunity has to be named
  // by WHAT it is or by WHERE it is. Either alone is a usable row; neither is not.
  const named = oppTitle.trim() !== "" || locationName.trim() !== "";
  const canSubmit = named && !emptiedSet && !busy;

  /**
   * The optional writes that ride their own endpoints, run in order and only when they actually changed,
   * then close. Sequential rather than parallel so a failure leaves the LATER steps unattempted (and the
   * form open on a still-accurate state) instead of half-applying two independent mutations.
   */
  const runFollowUps = (oppId: string) => {
    const steps: Array<(next: () => void) => void> = [];
    if (setChanged)
      steps.push((next) =>
        setInviteeSet.mutate(
          { oppId, personIds: invitees },
          { onSuccess: next },
        ),
      );
    // AFTER the set: it is what adds anyone genuinely new, and it leaves a re-added invitee's spent row
    // exactly where it was (an invite that already exists is skipped) — which is what this step replaces.
    if (editing && reAsked.length > 0)
      steps.push((next) =>
        reAsk.mutate({ oppId, personIds: reAsked }, { onSuccess: next }),
      );
    if (moneyChanged)
      steps.push((next) =>
        setMoney.mutate(
          { oppId, moneyCents, moneyNotes: moneyNotesValue },
          { onSuccess: next },
        ),
      );
    const run = (i: number) =>
      i === steps.length ? onClose() : steps[i]!(() => run(i + 1));
    run(0);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    const body: CreateOpportunityRequest = {
      type,
      title: oppTitle.trim() === "" ? null : oppTitle.trim(),
      // Blank CLEARS (null) rather than sending an empty string: an opportunity with no date yet is a
      // record we now hold honestly, and clearing a date on edit also takes the accepted invitees' calendar
      // rows back with it (server-side, through the uninvite gate).
      date: date.trim() === "" ? null : date,
      startTime: startTime.trim() === "" ? null : startTime,
      endTime: endTime.trim() === "" ? null : endTime,
      notes: notes.trim() === "" ? null : notes,
      locationName: locationName.trim() === "" ? null : locationName,
      meetingLink: isOnline && meetingLink.trim() !== "" ? meetingLink : null,
      ...(isPr ? { prSubtype, prFormat } : {}),
      // Invitees ride the CREATE: opp_visibility admits creator + invitees only, so a second call that
      // failed would strand an opportunity nobody else can see.
      invitees: invitees.map((personId) => ({ personId })),
    };
    // Anything with a server id — an opportunity opened for edit, OR one this modal already created on a
    // previous submit whose follow-ups failed — takes the PUT path. Only a modal that has never saved
    // creates.
    const savedOppId = opp?.opportunityId ?? createdOppId;
    if (savedOppId) {
      // The PUT is a FULL replace, so it carries the complete field set plus the untouched status —
      // a partial body would clobber the date/type. Invitees and money ride their OWN endpoints, chained
      // after: the invitee SET adds the new, uninvites the dropped, and clears a removed accepter's calendar
      // row via the uninvite gate; money is the money-gated sub-resource. The modal closes only when
      // everything landed; a failed link surfaces its own toast and leaves the form open.
      const { invitees: _drop, ...fields } = body;
      const put: UpdateOpportunityRequest = {
        ...fields,
        status: opp?.status ?? "open",
      };
      update.mutate(
        { oppId: savedOppId, body: put },
        { onSuccess: () => runFollowUps(savedOppId) },
      );
      return;
    }
    // Money can only be written once the create has minted the id, so it chains the same way. The id is
    // recorded BEFORE the follow-ups run, so a follow-up failure cannot strand this modal in create mode.
    create.mutate(body, {
      onSuccess: (created) => {
        setCreatedOppId(created.opportunityId!);
        runFollowUps(created.opportunityId!);
      },
    });
  };

  // A FormDialog (ART-251): a bottom sheet on a phone (OpsCalendar, the mobile home cards), a Modal on desktop.
  return (
    <>
      <FormDialog
        title={title}
        size="lg"
        onClose={onClose}
        bodyAs="form"
        onSubmit={submit}
        footer={
          <>
            {editing && (
              <Button
                type="button"
                variant="ghost"
                className={styles.footerDanger}
                onClick={() => setConfirmingCancel(true)}
              >
                Cancel Opportunity
              </Button>
            )}
            <DialogCancel />
            <Button type="submit" disabled={!canSubmit} loading={busy}>
              {editing || createdOppId ? "Save" : submitLabel}
            </Button>
          </>
        }
      >
        <Picker label="Type" value={type} onChange={setType} options={TYPES} />

        {/* WHAT it is. The location below is WHERE — either one names the row, and the card prints
              "Title · City, ST" from the pair. */}
        <Field
          label="Title"
          type="text"
          value={oppTitle}
          onChange={(e) => setOppTitle(e.target.value)}
          placeholder="e.g. Full-band rehearsal"
          maxLength={200}
        />

        <div className={styles.row}>
          <Field
            label="Date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          <Field
            label="Start time"
            type="time"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
          />
          <Field
            label="End time"
            type="time"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
          />
        </div>

        {isPr && (
          <div className={styles.row}>
            <Picker
              label="PR type"
              value={prSubtype}
              onChange={setPrSubtype}
              options={PR_SUBTYPES}
            />
            <Picker
              label="Format"
              value={prFormat}
              onChange={setPrFormat}
              options={PR_FORMATS}
            />
          </div>
        )}

        {/* An ONLINE opportunity still needs a name — "Song Exploder", not a bare URL — so the location
              field stays and the meeting link is an ADDITION beside it. Swapping them meant an online PR
              stored no name at all and rendered as a pill, a date and nothing else. */}
        <Field
          label="Location"
          type="text"
          value={locationName}
          onChange={(e) => setLocationName(e.target.value)}
          placeholder={
            isOnline ? "City, ST — or leave blank" : "Venue or place name"
          }
        />
        {isOnline && (
          <Field
            label="Meeting link"
            type="url"
            value={meetingLink}
            onChange={(e) => setMeetingLink(e.target.value)}
            placeholder="Paste the meeting link"
          />
        )}

        {canSeeMoney && (
          <div className={styles.moneyRow}>
            <Field
              label="Money ($)"
              type="number"
              inputMode="decimal"
              min={0}
              step="0.01"
              value={money}
              onChange={(e) => setMoneyEdit(e.target.value)}
              placeholder="Optional"
              aria-describedby={showMoneyHint ? moneyHintId : undefined}
            />
            {/* The half a cents column cannot hold. The legacy modal's ONE field had to be both, and its
                  placeholder said so — "Guarantee or note (e.g. $500, door split)". Now the amount is
                  typed and this carries the terms. */}
            <Field
              label="Money notes"
              value={moneyNotes}
              onChange={(e) => setMoneyNotesEdit(e.target.value)}
              placeholder="e.g. door split, plus 10% of merch"
            />
            {showMoneyHint && (
              <p id={moneyHintId} className={styles.moneyHint}>
                Only the money team sees the money on this opportunity.
              </p>
            )}
          </div>
        )}

        {/* ART-176: invite band crew AND the label/management/booking people reaching this band — the
            `invitable` source, not the band-team-only `crew` one. */}
        <CrewInviteePicker
          value={invitees}
          onChange={changeInvitees}
          source="invitable"
          searchPlaceholder="Search crew & team…"
        />
        {emptiedSet && (
          <p className={styles.pickerBlock} role="alert">
            An opportunity needs at least one invitee — to call the whole thing
            off, use Cancel Opportunity below.
          </p>
        )}

        <Textarea
          label="Notes"
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </FormDialog>
      {confirmingCancel && (
        <ConfirmDialog
          title="Cancel this opportunity?"
          description="It disappears from every invitee's dashboard, and any date they accepted comes off their calendar. This can't be undone."
          confirmLabel="Cancel Opportunity"
          loading={destroy.isPending}
          onConfirm={() =>
            destroy.mutate(opp!.opportunityId!, {
              onSuccess: () => {
                setConfirmingCancel(false);
                onClose();
              },
            })
          }
          onCancel={() => setConfirmingCancel(false)}
        />
      )}
    </>
  );
}
